import type { BulkUploadTwinAssetsResult } from '@repo/front-end-services';

/**
 * The page's record of the last upload in this session, for the Equipment step's file card
 * (`equipment-page-stage`, R6): the file's name, when it landed, and how many rows it created and
 * updated — shown until the server's record of the upload has been read.
 *
 * What an upload DID — refused lines, renames, rows the file left out — is the import summary's,
 * read from that record (`equipment-page-landed`, `equipment-page-reimport`). The refused-row band,
 * its corrections and dismissals that this module once projected went with `equipment-page-reimport`.
 *
 * Every field read is optional on `BulkUploadTwinAssetsResult`: a middleware that has not deployed a
 * field yet is the normal state around a merge, so absence is "not reported", never a claim.
 */
export interface PageImportAttempt {
  /** How many register rows the upload created. */
  createdCount: number;
  /** How many existing rows the upload updated. */
  updatedCount: number;
  /**
   * Every item reported whether it was created or updated. False when any did not — a middleware
   * that has not deployed `outcome` — and then the counts are not shown as the file's.
   */
  outcomeKnown: boolean;
  /**
   * The uploaded file's name and when its answer arrived. Held in browser memory, so a reload loses
   * them and the card falls back to the record's — the durable one is
   * `equipment-page-import-record`'s.
   */
  fileName?: string;
  uploadedAt?: Date;
}

/**
 * The file card's record of an upload, from the response the uploader just received: created and
 * updated counts, and whether the middleware reported both, from its items. An item whose `outcome`
 * is missing — or is neither word — makes the counts unknown. A malformed item never throws: this
 * runs inside the uploader's own `try`, where a throw would report a landed upload as failed.
 */
export const toImportAttempt = (result: BulkUploadTwinAssetsResult): PageImportAttempt => {
  let createdCount = 0;
  let updatedCount = 0;
  let outcomeKnown = true;
  for (const item of result.items ?? []) {
    if (item?.outcome === 'created') {
      createdCount += 1;
    } else if (item?.outcome === 'updated') {
      updatedCount += 1;
    } else {
      outcomeKnown = false;
    }
  }
  return { createdCount, updatedCount, outcomeKnown };
};
